import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { mergeLanding, SECTION_LABELS } from "@/lib/landingDefaults";
import { useWhatsAppConfig } from "@/components/WhatsAppButton";
import { Hero, QuickEnquiry, Featured, Packages, WhyJoin, CustomPlan, Partner, ICONS } from "@/components/landing/Sections";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Star } from "lucide-react";
import { MemberEnquiryContext } from "@/lib/waTemplates";

const RENDER = { hero: Hero, quick: QuickEnquiry, featured: Featured, packages: Packages, why: WhyJoin, custom: CustomPlan, partner: Partner };

export default function PortalSections(props) {
  const [templates, setTemplates] = useState({});
  useEffect(() => { api.get("/public/wa-templates").then(({ data }) => setTemplates(data || {})).catch(() => {}); }, []);
  return (
    <MemberEnquiryContext.Provider value={{ member: { name: props.memberName, id: props.memberId }, templates }}>
      <PortalLayout {...props} />
    </MemberEnquiryContext.Provider>
  );
}

function PortalLayout({ deals, memberItems = [] }) {
  const [content, setContent] = useState(null);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const wa = useWhatsAppConfig();
  const waNumber = wa?.enabled ? wa.number : null;

  useEffect(() => {
    api.get("/public/landing").then(({ data }) => setContent(mergeLanding(data))).catch(() => setContent(mergeLanding({})));
    const mq = window.matchMedia("(max-width: 767px)");
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const memberById = Object.fromEntries(memberItems.map((m) => [m.id, m]));
  const memberKeys = memberItems.map((m) => m.id).join(",");
  useEffect(() => {
    if (open?.startsWith("member:") && !memberKeys.split(",").includes(open.slice(7))) setOpen(null);
  }, [open, memberKeys]);

  const dealsNode = (title) => (typeof deals === "function" ? deals(title) : deals);
  if (!content) return <div className="mt-10">{dealsNode()}</div>;
  const { order, sections } = content.portal;
  const visible = order.filter((id) => sections[id]?.show);
  const expanded = visible.filter((id) => sections[id].mode === "expanded");
  const icons = [...memberItems.map((m) => `member:${m.id}`), ...visible.filter((id) => sections[id].mode === "icon")];
  const meta = (id) => {
    const m = memberById[id.slice(7)];
    return m ? { label: m.label, I: m.icon } : { label: sections[id]?.label || SECTION_LABELS[id], I: ICONS[sections[id]?.icon] || Star };
  };
  const body = (id, embedded = false) => {
    if (id.startsWith("member:")) return <div className="px-4 sm:px-6">{memberById[id.slice(7)]?.node}</div>;
    if (id === "deals") return dealsNode(content.deals?.title);
    const S = RENDER[id];
    return S ? <S c={content[id]} wa={waNumber} embedded={embedded} /> : null;
  };
  const toggle = (id) => setOpen((cur) => (cur === id ? null : id));

  const bar = icons.length > 0 && (
    <nav aria-label="More from Zola" data-testid="portal-icon-bar"
         className="md:static md:mt-8 fixed inset-x-0 z-30 md:z-auto md:rounded-2xl md:border"
         style={{ bottom: mobile ? "calc(64px + env(safe-area-inset-bottom))" : undefined, background: "var(--surface)",
                  borderTop: "1px solid var(--border)", borderColor: "var(--border)" }}>
      <ul className="flex md:flex-wrap overflow-x-auto md:overflow-visible gap-1 p-1.5 md:p-2">
        {icons.map((id) => {
          const { label, I } = meta(id);
          const active = open === id;
          const tid = id.replace("member:", "member-");
          return (
            <li key={id} className="flex-1 md:flex-none min-w-[68px]">
              <button type="button" onClick={() => toggle(id)} aria-expanded={active} aria-controls="portal-panel"
                      data-testid={`portal-icon-${tid}`}
                      className="w-full flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 h-12 md:h-10 px-2 md:px-4 rounded-xl text-[10px] md:text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2"
                      style={active ? { background: "var(--navy)", color: "#fff" } : { color: "var(--text)" }}>
                <I className="h-5 w-5 md:h-4 md:w-4" /><span className="truncate max-w-[72px] md:max-w-none">{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );

  return (
    <div data-testid="portal-sections">
      {bar}
      {expanded.map((id) => (
        <div key={id} className={`mt-10 ${id === "deals" ? "" : "portal-embed"}`} data-testid={`portal-expanded-${id}`}>{body(id)}</div>
      ))}
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)} modal={mobile}>
        <SheetContent id="portal-panel" side={mobile ? "bottom" : "right"} data-testid="portal-panel" aria-describedby={undefined}
                      onInteractOutside={(e) => { if (e.target?.closest?.("[data-testid=portal-icon-bar]")) e.preventDefault(); }}
                      className={`overflow-y-auto ${mobile ? "max-h-[85vh] rounded-t-2xl" : "w-full sm:max-w-[620px] shadow-2xl"}`}>
          <SheetHeader><SheetTitle className="sr-only">{open && meta(open).label}</SheetTitle></SheetHeader>
          <div className="portal-embed -mx-4 sm:-mx-6 pt-4 pb-6">{open && body(open, true)}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
